import { useState } from "react";
import { Link } from "react-router-dom";
import WeatherSourceBadge from "../../../components/weather/public/WeatherSourceBadge.jsx";
import WeatherSourceDetailModal from "../../../components/weather/public/WeatherSourceDetailModal.jsx";
import {
  WEATHER_TRANSPARENCY_ATTRIBUTION,
  WEATHER_TRANSPARENCY_COVERAGE,
  WEATHER_TRANSPARENCY_FALLBACK,
  WEATHER_TRANSPARENCY_FRESHNESS,
  WEATHER_TRANSPARENCY_PAGE,
  WEATHER_TRANSPARENCY_RADAR_VS_ALERT,
  WEATHER_TRANSPARENCY_SAFETY_FOOTER,
  WEATHER_TRANSPARENCY_SOURCE_SECTIONS,
  WEATHER_TRANSPARENCY_VALLEY_SWITCH,
  WEATHER_TRUST_LABEL_DEFINITIONS,
} from "../../../constants/weatherPublicCopy.js";
import { useWeatherPublicSeo } from "../../../hooks/useWeatherPublicSeo.js";
import WeatherPublicLayout from "./WeatherPublicLayout.jsx";

const PAGE_SECTIONS = [
  { id: "weather-trust-labels", label: "Source labels" },
  { id: "weather-data-sources", label: "Data sources" },
  { id: "weather-valley-switch", label: "Source switching" },
  { id: "weather-radar-vs-alert", label: "Radar vs alerts" },
  { id: "weather-freshness", label: "Freshness" },
  { id: "weather-fallback", label: "Fallback order" },
  { id: "weather-coverage", label: "Coverage" },
  { id: "weather-attribution", label: "Attribution" },
];

const WeatherSourceTransparencyPage = () => {
  const [detailSource, setDetailSource] = useState(null);

  useWeatherPublicSeo({
    title: `${WEATHER_TRANSPARENCY_PAGE.title} · Midpoint Map`,
    description: WEATHER_TRANSPARENCY_PAGE.subtitle,
    canonicalPath: WEATHER_TRANSPARENCY_PAGE.path,
  });

  return (
    <WeatherPublicLayout title={WEATHER_TRANSPARENCY_PAGE.title}>
      <nav className="weather-public-toc" aria-label="On this page">
        <ul>
          {PAGE_SECTIONS.map((section) => (
            <li key={section.id}>
              <a href={`#${section.id}`}>{section.label}</a>
            </li>
          ))}
        </ul>
      </nav>

      <section className="weather-public-hero">
        <p className="weather-public-eyebrow">{WEATHER_TRANSPARENCY_PAGE.eyebrow}</p>
        <h1>{WEATHER_TRANSPARENCY_PAGE.title}</h1>
        <p className="weather-public-subtitle">{WEATHER_TRANSPARENCY_PAGE.subtitle}</p>
        <p>{WEATHER_TRANSPARENCY_PAGE.intro}</p>
        <p className="weather-public-muted">{WEATHER_TRANSPARENCY_PAGE.principle}</p>
      </section>

      <section className="weather-public-section" aria-labelledby="weather-trust-labels">
        <h2 id="weather-trust-labels">Understanding source labels</h2>
        <p className="weather-public-muted">
          These badges appear on map layers, alerts, and summaries so you know where information comes
          from.
        </p>
        <div className="weather-public-glossary">
          {WEATHER_TRUST_LABEL_DEFINITIONS.map((item) => (
            <div key={item.label} className="weather-public-glossary-item">
              <WeatherSourceBadge tone={item.tone} label={item.label} />
              <p>{item.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="weather-public-section" aria-labelledby="weather-data-sources">
        <h2 id="weather-data-sources">Data sources</h2>
        {WEATHER_TRANSPARENCY_SOURCE_SECTIONS.map((source) => (
          <article key={source.id} className="weather-public-source-card">
            <div className="weather-public-source-card__title-row">
              <h3>{source.title}</h3>
              <WeatherSourceBadge tone={source.badgeTone} label={source.badgeLabel} />
            </div>
            <p>{source.body}</p>
            <dl className="weather-public-source-meta">
              {source.publicUseCase ? (
                <>
                  <dt>Use</dt>
                  <dd>{source.publicUseCase}</dd>
                </>
              ) : null}
              {source.publicCoverage ? (
                <>
                  <dt>Coverage</dt>
                  <dd>{source.publicCoverage}</dd>
                </>
              ) : null}
              {source.publicLimitations ? (
                <>
                  <dt>Limitation</dt>
                  <dd>{source.publicLimitations}</dd>
                </>
              ) : null}
              {source.publicAttribution ? (
                <>
                  <dt>Attribution</dt>
                  <dd>{source.publicAttribution}</dd>
                </>
              ) : null}
            </dl>
            <button
              type="button"
              className="weather-public-source-card__detail-btn"
              onClick={() => setDetailSource(source)}
            >
              More about this source
            </button>
          </article>
        ))}
      </section>

      <section className="weather-public-section" aria-labelledby="weather-valley-switch">
        <h2 id="weather-valley-switch">{WEATHER_TRANSPARENCY_VALLEY_SWITCH.title}</h2>
        <p>{WEATHER_TRANSPARENCY_VALLEY_SWITCH.body}</p>
        <ul className="weather-public-steps">
          {WEATHER_TRANSPARENCY_VALLEY_SWITCH.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      <section className="weather-public-section" aria-labelledby="weather-radar-vs-alert">
        <h2 id="weather-radar-vs-alert">{WEATHER_TRANSPARENCY_RADAR_VS_ALERT.title}</h2>
        <p>{WEATHER_TRANSPARENCY_RADAR_VS_ALERT.lead}</p>
        <ul className="weather-public-steps">
          {WEATHER_TRANSPARENCY_RADAR_VS_ALERT.bullets.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      <section className="weather-public-section" aria-labelledby="weather-freshness">
        <h2 id="weather-freshness">{WEATHER_TRANSPARENCY_FRESHNESS.title}</h2>
        <p>{WEATHER_TRANSPARENCY_FRESHNESS.intro}</p>
        <ul className="weather-public-freshness-list">
          {WEATHER_TRANSPARENCY_FRESHNESS.examples.map((item) => (
            <li key={item.label}>
              <strong>{item.label}:</strong> <span className="weather-public-muted">{item.example}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="weather-public-section" aria-labelledby="weather-fallback">
        <h2 id="weather-fallback">{WEATHER_TRANSPARENCY_FALLBACK.title}</h2>
        <p>{WEATHER_TRANSPARENCY_FALLBACK.body}</p>
        <ul className="weather-public-steps">
          {WEATHER_TRANSPARENCY_FALLBACK.steps.map((step) => (
            <li key={step.label}>
              <strong>{step.label}:</strong> {step.detail}
            </li>
          ))}
        </ul>
      </section>

      <section className="weather-public-section" aria-labelledby="weather-coverage">
        <h2 id="weather-coverage">{WEATHER_TRANSPARENCY_COVERAGE.title}</h2>
        <ul className="weather-public-steps">
          {WEATHER_TRANSPARENCY_COVERAGE.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      <section className="weather-public-section" aria-labelledby="weather-attribution">
        <h2 id="weather-attribution">{WEATHER_TRANSPARENCY_ATTRIBUTION.title}</h2>
        <ul className="weather-public-attribution-list">
          {WEATHER_TRANSPARENCY_ATTRIBUTION.items.map((item) => (
            <li key={item.label}>
              <strong>{item.label}:</strong> {item.text}
            </li>
          ))}
        </ul>
      </section>

      <section className="weather-public-callout" aria-label="Safety and authority boundary">
        <p>{WEATHER_TRANSPARENCY_SAFETY_FOOTER}</p>
      </section>

      <div className="weather-public-cta-row">
        <Link to="/" className="weather-public-cta">
          Open weather map
        </Link>
        <Link to="/" className="weather-public-cta weather-public-cta--secondary">
          Back to Midpoint
        </Link>
      </div>

      <WeatherSourceDetailModal
        source={detailSource}
        isOpen={Boolean(detailSource)}
        onClose={() => setDetailSource(null)}
      />
    </WeatherPublicLayout>
  );
};

export default WeatherSourceTransparencyPage;
